import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { cx } from '../lib/cx.ts';

type ModalBaseProps = {
  open: boolean;
  onClose: () => void;
  /** Escape and backdrop clicks are ignored. For destructive confirmations. */
  dismissible?: boolean;
  children: ReactNode;
  className?: string;
};

/**
 * Wraps the native `<dialog>` element, which gives us the focus trap, the
 * inert background, Escape handling and correct screen-reader semantics without
 * reimplementing any of it. Dialog and Sheet are the same primitive with
 * different placement.
 */
export function ModalBase({
  open,
  onClose,
  dismissible = true,
  children,
  className,
}: ModalBaseProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;

    function handleCancel(event: Event): void {
      // Escape. Suppress it entirely when the dialog must be answered.
      if (!dismissible) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      onClose();
    }

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [dismissible, onClose]);

  return (
    <dialog
      ref={ref}
      onClick={(event) => {
        // A click landing on the dialog element itself is a backdrop click;
        // anything inside the content bubbles from a child.
        if (dismissible && event.target === ref.current) onClose();
      }}
      className={cx(
        'bg-transparent p-0 text-text backdrop:bg-overlay',
        'max-h-dvh max-w-dvw',
        className,
      )}
    >
      {children}
    </dialog>
  );
}
